import React, { useEffect, useRef, useState } from "react";
import styles from "./pageNav.module.css";
import { Button } from "../UI";

// Sticky "on this page" bar. Highlights the section currently in view.
const PageNav = ({ label = "On this page", links, cta }) => {
  const [active, setActive] = useState(links[0]?.id);
  const listRef = useRef(null);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--scroll-offset", "148px");
    return () => root.style.removeProperty("--scroll-offset");
  }, []);

  useEffect(() => {
    const els = links.map((l) => document.getElementById(l.id)).filter(Boolean);
    if (!els.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-140px 0px -55% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [links]);

  // Keep the active link visible inside the horizontally scrolling list.
  // Only the list scrolls: scrollIntoView would also scroll the window and
  // pull the page down to this bar when a page opens.
  useEffect(() => {
    const list = listRef.current;
    const link = list?.querySelector(`[data-id="${active}"]`);
    if (!list || !link) return;
    const left = link.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
    const right = left + link.offsetWidth;
    if (left < list.scrollLeft) list.scrollTo({ left, behavior: "smooth" });
    else if (right > list.scrollLeft + list.clientWidth)
      list.scrollTo({ left: right - list.clientWidth, behavior: "smooth" });
  }, [active]);

  return (
    <nav className={styles.bar} aria-label={label}>
      <div className={styles.inner}>
        <ul className={styles.list} ref={listRef}>
          {links.map((l) => (
            <li key={l.id}>
              <a
                href={`#${l.id}`}
                data-id={l.id}
                className={styles.link}
                aria-current={active === l.id ? "location" : undefined}
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
        {cta && (
          <Button href={cta.href} variant="primary" className={styles.cta}>
            {cta.label}
          </Button>
        )}
      </div>
    </nav>
  );
};

export default PageNav;
